// API service for workflow operations
import { WorkflowCatalogItem, WorkflowDetails, WorkflowJobRequest, ValidationError } from '../types/workflow';
import { getApiBaseUrl } from '../config/environment';

const API_BASE = getApiBaseUrl();

export interface WorkflowListResponse {
  workflows: WorkflowCatalogItem[];
  count: number;
}

export class WorkflowService {
  private accessToken: string | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private getHeaders(): HeadersInit {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  async listWorkflows(tenantId: string, params?: {
    tags?: string[];
    status?: 'active' | 'deprecated';
  }): Promise<WorkflowListResponse> {
    console.log('📡 [WorkflowService] listWorkflows() called', { tenantId, params, hasToken: !!this.accessToken });

    const queryParams = new URLSearchParams();
    // Default to 'active' status to match new API behavior
    queryParams.append('status', params?.status || 'active');
    if (params?.tags && params.tags.length > 0) {
      queryParams.append('tags', params.tags.join(','));
    }

    // Use new tenant-scoped endpoint
    const url = `${API_BASE}/api/tenants/${tenantId}/workflows?${queryParams.toString()}`;
    console.log('📡 [WorkflowService] Fetching workflows from:', url);
    console.log('📡 [WorkflowService] Headers:', this.getHeaders());

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    console.log('📡 [WorkflowService] Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch workflows' }));
      console.error('❌ [WorkflowService] Error fetching workflows:', error);
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    console.log('✅ [WorkflowService] Workflows loaded:', {
      count: data.workflows?.length || 0,
      workflows: data.workflows
    });
    return data;
  }

  async getWorkflowDetails(workflowId: string, version?: string, tenantId?: string): Promise<WorkflowDetails> {
    // Use new WorkflowManagement endpoint (version parameter is ignored for now - Phase 7 MVP doesn't support versioning)
    let url = `${API_BASE}/api/workflows/${workflowId}`;
    
    // If tenantId is provided, add it as query parameter (for platform admin cross-tenant access)
    if (tenantId) {
      url += `?tenantId=${encodeURIComponent(tenantId)}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch workflow details' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();

    // Transform new API response to match expected WorkflowDetails format
    return {
      workflowId: data.metadata.rowKey,
      version: data.metadata.version || '1.0.0',
      manifest: data.package.manifest,
      uiConfig: {
        jsonSchema: data.package.schemas.jsonSchema,
        uiSchema: data.package.schemas.uiSchema
      },
      checksum: '', // Not used in new API
      publishedAt: data.metadata.createdAt,
      publishedBy: data.metadata.createdBy
    };
  }

  async validateInputs(
    workflowId: string,
    inputs: Record<string, any>,
    version?: string
  ): Promise<{ valid: boolean; errors?: ValidationError[] }> {
    // Client-side validation against JSON Schema
    // This will be implemented after we get the workflow details
    // For now, return valid
    return { valid: true };
  }

  /**
   * Upload a file (data URL) to Blob Storage
   */
  private async uploadFile(dataUrl: string, filename?: string): Promise<string> {
    const url = `${API_BASE}/api/upload/file`;

    console.log('📤 Uploading file to Blob Storage...');

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify({ dataUrl, filename }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to upload file' }));
      console.error('❌ File upload error:', error);
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ File uploaded:', result.blobUrl);
    return result.blobUrl;
  }

  /**
   * Process inputs to upload any data URLs to Blob Storage
   */
  private async processInputValue(value: any): Promise<any> {
    if (typeof value === 'string' && value.startsWith('data:')) {
      return this.uploadFile(value);
    }

    if (value && typeof value === 'object' && typeof value.dataUrl === 'string') {
      return this.uploadFile(value.dataUrl, value.filename);
    }

    if (Array.isArray(value)) {
      return Promise.all(value.map(item => this.processInputValue(item)));
    }

    if (value && typeof value === 'object') {
      const entries = await Promise.all(
        Object.entries(value).map(async ([key, nestedValue]) => [key, await this.processInputValue(nestedValue)])
      );
      return Object.fromEntries(entries);
    }

    return value;
  }

  private async processInputs(inputs: Record<string, any>): Promise<Record<string, any>> {
    const processedInputs: Record<string, any> = {};

    for (const [key, value] of Object.entries(inputs)) {
      processedInputs[key] = await this.processInputValue(value);
    }

    return processedInputs;
  }

  async createJob(request: WorkflowJobRequest, tenantId?: string): Promise<any> {
    // Process inputs to upload any files to Blob Storage first
    console.log('🔍 Processing inputs for file uploads...');
    const processedInputs = await this.processInputs(request.inputs);

    // Use NEW ExecuteWorkflowV2 endpoint for Phase 7 workflows
    const url = `${API_BASE}/api/v2/workflows/${request.workflowId}/execute`;

    // Prepare request body with optional tenantId (for platform admin cross-tenant execution)
    const requestBody: any = { inputs: processedInputs };
    if (tenantId) {
      requestBody.tenantId = tenantId;
    }

    console.log('📡 API Call: POST /v2/workflows/{id}/execute', {
      url,
      request: requestBody,
      headers: this.getHeaders()
    });

    const response = await fetch(url, {
      method: 'POST',
      headers: this.getHeaders(),
      body: JSON.stringify(requestBody),
    });

    console.log('📡 API Response:', {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to create job' }));
      console.error('❌ API Error:', error);
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ API Success:', result);
    return result;
  }

  async getJobStatus(jobId: string): Promise<any> {
    const response = await fetch(`${API_BASE}/api/jobs/${jobId}`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch job status' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    return response.json();
  }

  /**
   * Live-progress poll: all of the tenant's non-terminal jobs in one call
   * (single-partition query server-side, no quota lookup). Replaces per-job
   * getJobStatus polling - one request per tenant per tick instead of one
   * per job. Also returns a queueDepth hint while jobs are still queued.
   */
  async getActiveJobs(): Promise<{ jobs: any[]; queueDepth: number }> {
    const response = await fetch(`${API_BASE}/api/jobs?active=true`, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch active jobs' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return { jobs: data.jobs || [], queueDepth: data.queueDepth || 0 };
  }

  async getJobHistory(limit: number = 10, tenantId?: string): Promise<any[]> {
    let url = `${API_BASE}/api/jobs?limit=${limit}`;
    
    // If tenantId is provided, add it as query parameter (for platform admin cross-tenant access)
    if (tenantId) {
      url += `&tenantId=${encodeURIComponent(tenantId)}`;
    }
    
    const response = await fetch(url, {
      method: 'GET',
      headers: this.getHeaders(),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Failed to fetch job history' }));
      throw new Error(error.message || `HTTP ${response.status}`);
    }

    const data = await response.json();
    return data.jobs || [];
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
